/**
 * The Shopify App Store install handoff, as the signup page explains it.
 *
 * The OAuth callback sends a merchant here with `?shopify=connected` (store
 * parked, waiting for an account), `?shopify=pending` (the OAuth state was not
 * found — expired or a second tab) or `?shopify=error&reason=…` (Redis down,
 * scopes refused). Only the first ever rendered a message; the other two
 * showed the bare form, so the merchant never learned the install had failed
 * and the reviewer saw an app that "did nothing" after install.
 */
import type { ReactElement } from 'react';
import { t } from '../../i18n';

interface Props {
  status: string | null;
  shop: string | null;
  name: string | null;
  reason: string | null;
}

export function ShopifyHandoffNotice({ status, shop, name, reason }: Props): ReactElement | null {
  if (status === 'connected') {
    return (
      <div className="mb-6 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
        <p className="text-sm text-emerald-800">
          {t('integrations.handoff.connected', { store: name || shop || t('integrations.handoff.yourStore') })}
        </p>
      </div>
    );
  }
  if (status === 'pending') {
    return (
      <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-800">
          {t('integrations.handoff.pending', { store: shop || t('integrations.handoff.yourStore') })}
        </p>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm text-red-800">
          {reason
            ? t('integrations.handoff.errorReason', { reason: reason.replace(/_/g, ' ') })
            : t('integrations.handoff.error')}
        </p>
      </div>
    );
  }
  return null;
}
